interface HeroCarouselSurfaceConfig {
  palette?: App.Design.PaletteType
  carouselPalette?: App.Design.PaletteType
  background?: unknown
  videoSourceUrl?: string
  // Present on real configs but must not trip the custom-surface gate by themselves.
  campaignId?: string
  title?: unknown
  logo?: unknown
}

interface SiteTakeoverThemeSurface {
  backgroundRef?: string
  mobileBackgroundRef?: string
}

function hasTakeoverThemeBackground(siteTakeoverTheme: unknown): boolean {
  if (!siteTakeoverTheme || typeof siteTakeoverTheme !== 'object') {
    return false
  }

  const theme = siteTakeoverTheme as SiteTakeoverThemeSurface
  return !!(theme.backgroundRef || theme.mobileBackgroundRef)
}

export type ExclusiveOfferCarouselTileStyle = 'card-opaque' | 'card-default'

export interface ExclusiveOfferCarouselSurface {
  isRebrandExclusiveSurface: boolean
  containerPalette: App.Design.PaletteType
  titleSheetPalette: App.Design.PaletteType
  carouselSheetPalette: App.Design.PaletteType
  tileStyle: ExclusiveOfferCarouselTileStyle
  useOpaqueCarousel: boolean
}

/**
 * True when a takeover actually owns the hero visual surface (background image
 * or video). Loyalty / copy-only takeovers set palette or a lab theme id without
 * those assets; they must keep the default exclusive-carousel rebrand teal.
 */
export function hasCustomHeroOfferCarouselSurface({
  siteTakeoverTheme,
  heroCarouselConfig,
}: {
  siteTakeoverTheme?: unknown
  heroCarouselConfig?: HeroCarouselSurfaceConfig | null
}): boolean {
  return !!(
    hasTakeoverThemeBackground(siteTakeoverTheme)
    || heroCarouselConfig?.background
    || heroCarouselConfig?.videoSourceUrl
  )
}

/**
 * Structural exclusive-carousel surface for rebrand teal vs legacy inverse.
 * Keep body.app-palette-rebrand CSS dual-gated with `.rebrand-highlight` separately.
 */
export function getExclusiveOfferCarouselSurface({
  isRebrand,
  hasCustomHeroSurface = false,
  palette = 'inverse',
  carouselPalette,
}: {
  isRebrand: boolean
  hasCustomHeroSurface?: boolean
  palette?: App.Design.PaletteType
  carouselPalette?: App.Design.PaletteType
}): ExclusiveOfferCarouselSurface {
  const isRebrandExclusiveSurface = isRebrand && !hasCustomHeroSurface
  const useOpaqueCarousel = !!carouselPalette || isRebrandExclusiveSurface

  return {
    isRebrandExclusiveSurface,
    containerPalette: isRebrandExclusiveSurface ? 'default' : palette,
    titleSheetPalette: isRebrandExclusiveSurface ? 'inverse' : palette,
    carouselSheetPalette: useOpaqueCarousel ? 'default' : 'inverse',
    tileStyle: useOpaqueCarousel ? 'card-opaque' : 'card-default',
    useOpaqueCarousel,
  }
}
